import { Button, Grid, Typography } from "@mui/material";
import {
  ApiError,
  ApiErrorClass,
  ApiResponse,
  PlanTemplateWorkout,
  WorkoutCategories,
} from "@readyfit-common/models";
import {
  handleApiError,
  parseFitnessWorkout,
  parseRunWorkout,
  parseSwimWorkout,
} from "@readyfit-common/utils";
import { useFormik } from "formik";
import { useEffect, useState } from "react";
import { LoadingButton } from "src/grading/components";
import { WorkoutFormValues } from "src/grading/components/EditWorkoutModal/EditWorkoutModal";
import RFModal from "src/grading/components/RFModal/RFModal";
import WorkoutForm from "src/grading/components/WorkoutForm/WorkoutForm";
import { useAppDispatch } from "src/store/hooks";
import { showSnackbar } from "src/store/reducers/navigation/navigationSlice";
import {
  UpdateWorkoutLibraryRequest,
  useDeleteFromWorkoutLibraryMutation,
  useLazyGetWorkoutLibraryByIdQuery,
  useSaveToWorkoutLibraryMutation,
  useUpdateWorkoutLibraryMutation,
} from "src/store/reducers/training/trainingApiSlice";
import theme from "src/theme";
import * as Yup from "yup";
import useStyles from "./WorkoutModal.style";
import "react-time-picker/dist/TimePicker.css";
import "react-clock/dist/Clock.css";

interface ExerciseModalProps {
  open: boolean;
  onClose: () => void;
  isEditing?: boolean;
  workoutId?: string;
}

const validationSchema = Yup.object().shape({
  name: Yup.string().when("tabValue", ([tabValue], schema) => {
    return tabValue === 0
      ? schema.required("Name is required")
      : schema.notRequired();
  }),
  workoutCategoryId: Yup.number().when("tabValue", ([tabValue], schema) => {
    return tabValue === 0
      ? schema.required("Workout category is required")
      : schema.notRequired();
  }),
  workoutTypeId: Yup.number().when("tabValue", ([tabValue], schema) => {
    return tabValue === 0
      ? schema.required("Workout type is required")
      : schema.notRequired();
  }),
  distance: Yup.number().nullable(),
  estimatedTime: Yup.string().nullable(),
  description: Yup.string().required(),
  saveToWorkoutLibrary: Yup.boolean().required(),
});

const WorkoutModal = ({
  open,
  onClose,
  isEditing,
  workoutId,
}: ExerciseModalProps) => {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const [saveToWorkoutLibrary, { isLoading: saveToWorkoutLibraryLoading }] =
    useSaveToWorkoutLibraryMutation();
  const [updateWorkoutLibrary, { isLoading: updateWorkoutLibraryLoading }] =
    useUpdateWorkoutLibraryMutation();
  const [
    deleteFromWorkoutLibrary,
    { isLoading: deleteFromWorkoutLibraryLoading },
  ] = useDeleteFromWorkoutLibraryMutation();
  const [getWorkoutLibraryById, { data, isLoading: isWorkoutLibraryLoading }] =
    useLazyGetWorkoutLibraryByIdQuery();
  const [workout, setWorkout] = useState<PlanTemplateWorkout | null>(null);

  const initialValues: WorkoutFormValues = {
    name: data?.data?.workout?.name || "",
    workoutCategoryId: data?.data?.workout?.workoutCategoryId as number,
    workoutTypeId: data?.data?.workout?.workoutTypeId as number,
    distance: data?.data?.workout?.distance || null,
    estimatedTime: data?.data?.workout?.estimatedTime || null,
    description: data?.data?.workout?.description || "",
    exercises: data?.data?.workout?.workoutLibraryExercises || [],
    saveToWorkoutLibrary: false,
    tabValue: 0,
  };

  useEffect(() => {
    const fetchWorkout = async (id: string) => {
      const response = await getWorkoutLibraryById(id).unwrap();
      setWorkout(response.data?.workout);
    };

    if (isEditing && workoutId) {
      fetchWorkout(workoutId);
    }
  }, [isEditing, getWorkoutLibraryById, workoutId]);

  const formik = useFormik({
    initialValues,
    validationSchema,
    validateOnChange: false,
    enableReinitialize: true,
    onSubmit: async (values: WorkoutFormValues) => {
      try {
        let response: ApiResponse<void> | null = null;
        const workoutPayload = () => {
          switch (values.workoutCategoryId) {
            case WorkoutCategories.RUN:
              return parseRunWorkout(values);
            case WorkoutCategories.SWIM:
              return parseSwimWorkout(values);
            case WorkoutCategories.FITNESS:
              return parseFitnessWorkout(values);
            default:
              return values;
          }
        };
        // EDIT WORKOUT
        if (isEditing) {
          if (!workoutId) {
            throw new ApiErrorClass({
              message: "No workout id",
            });
          }

          const { name, ...rest } = workoutPayload();
          const shouldIncludeName = workout?.name !== name;

          const payload: UpdateWorkoutLibraryRequest = {
            ...(shouldIncludeName ? { name } : {}),
            ...rest,
            id: workoutId,
          };

          response = await updateWorkoutLibrary(payload).unwrap();
        } else {
          // ADD WORKOUT
          response = await saveToWorkoutLibrary(workoutPayload()).unwrap();
        }

        if (!response?.success)
          throw new Error(response?.message || "Error saving workout");

        dispatch(
          showSnackbar({
            open: true,
            message: "Workout saved successfully",
          }),
        );

        handleClose();
      } catch (error: unknown) {
        const typedError = error as ApiErrorClass;
        console.log("error", error);
        dispatch(
          showSnackbar({
            open: true,
            message: typedError?.data?.message || "Error saving workout",
            type: "error",
          }),
        );
      }
    },
  });

  const handleDelete = async () => {
    try {
      if (!workoutId) {
        throw new ApiErrorClass({
          message: "Exercise ID is required",
        });
      }
      await deleteFromWorkoutLibrary(workoutId).unwrap();
      dispatch(
        showSnackbar({
          open: true,
          message: "Exercise deleted successfully",
        }),
      );
      handleClose();
    } catch (error: unknown) {
      const typedError = error as ApiError;
      handleApiError(dispatch, typedError);
    }
  };

  const {
    values,
    setFieldValue,
    handleSubmit,
    errors,
    handleChange,
    resetForm,
  } = formik;

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <RFModal
      open={open}
      boxProps={{ className: classes.container }}
      onClose={handleClose}
      isLoading={isWorkoutLibraryLoading}
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Grid
        container
        component="form"
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleSubmit();
        }}
        rowGap={theme.spacing(3)}
      >
        <Typography variant="h5" fontSize={18}>
          {isEditing ? "Edit Workout" : "Add Workout"}
        </Typography>

        <Grid
          container
          component="form"
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleSubmit();
          }}
          rowGap={theme.spacing(3)}
          spacing={2}
        >
          <WorkoutForm
            values={values}
            errors={errors}
            handleChange={handleChange}
            setFieldValue={setFieldValue}
          />
        </Grid>

        <Grid
          container
          item
          xs={12}
          justifyContent="space-between"
          marginTop={theme.spacing(4)}
        >
          <Grid item>
            <Button
              className={` ${classes.button} ${classes.deleteButton}`}
              onClick={() => {
                if (isEditing) {
                  handleDelete();
                } else {
                  onClose();
                }
              }}
              variant="contained"
            >
              {isEditing ? "Delete" : "Cancel"}
            </Button>
          </Grid>

          <Grid item>
            <LoadingButton
              variant="contained"
              color="primary"
              className={classes.button}
              label="Save"
              onClick={() => handleSubmit()}
              loading={
                saveToWorkoutLibraryLoading ||
                deleteFromWorkoutLibraryLoading ||
                updateWorkoutLibraryLoading
              }
            />
          </Grid>
        </Grid>
      </Grid>
    </RFModal>
  );
};

export default WorkoutModal;
